import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Textarea,
  useToast,
  Box,
  Text,
  Image,
  IconButton,
  useColorModeValue,
  InputGroup,
  InputRightElement,
  Select,
  Badge,
  Flex,
  Spinner,
  InputLeftElement,
  Checkbox,
  Tooltip,
  Spacer,
  Divider,
  useBreakpointValue,
} from '@chakra-ui/react';
import { CloseIcon, AddIcon, RepeatIcon } from '@chakra-ui/icons';
import { useAuth } from '../context/AuthContext';
import { declarationsAPI, documentsAPI, declarationDetailsAPI } from '../utils/api';

const DeclarationFormModal = ({ 
  isOpen, 
  onClose, 
  type, 
  onDeclarationAdded, 
  userRole, 
  selectedCommercial, 
  commercials,
  editingDeclaration = null,
  onDeclarationUpdated = null,
  onTypeChange = null
}) => {
  const { commercial } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    montant: '',
    montant_allouer: '',
    description: '',
    date_valeur: '',
    photo: null,
    commercial_affected: selectedCommercial || '',
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  
  // Document selection state - single document only
  const [documents, setDocuments] = useState({
    facture: [],
    devis: [],
    proforma: []
  });
  const [selectedDocument, setSelectedDocument] = useState(null); // Single document selection
  const [selectedDocumentType, setSelectedDocumentType] = useState(null); // New state for document type selection
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documentsLoaded, setDocumentsLoaded] = useState(false);
  
  // Search and dropdown state
  const [searchTerms, setSearchTerms] = useState({
    facture: '',
    devis: '',
    proforma: ''
  });
  const [dropdownOpen, setDropdownOpen] = useState({
    facture: false,
    devis: false,
    proforma: false
  });

  // --- Add search state for each document type at the top level ---
  const [searchResults, setSearchResults] = useState({
    facture: [],
    devis: [],
    proforma: []
  });
  const [searchLoading, setSearchLoading] = useState({
    facture: false,
    devis: false,
    proforma: false
  });
  const [searchError, setSearchError] = useState({
    facture: null,
    devis: null,
    proforma: null
  });

  // Add at the top level:
  const [dbSearchActive, setDbSearchActive] = useState({
    facture: false,
    devis: false,
    proforma: false
  });
  const [dbSearchRequested, setDbSearchRequested] = useState({
    facture: false,
    devis: false,
    proforma: false
  });

  const bg = useColorModeValue('white', 'gray.800');
  const isEditing = !!editingDeclaration;

  let isAssignedDeclaration = false;
  if (editingDeclaration) {
    // Check if this declaration was assigned to this Commercial user by a Technico (commercial_affected = current user)
    isAssignedDeclaration =  editingDeclaration?.commercial_affected == commercial.id;
  }
    console.log('commercial', commercial);
    console.log('editingDeclaration', editingDeclaration);
    console.log('userRole', userRole);
    console.log('isEditing', isEditing);
    console.log('isAssignedDeclaration', isAssignedDeclaration);
  // Responsive direction for search controls (move out of renderDocumentSection)
  const searchControlsDirection = useBreakpointValue({ base: 'column', sm: 'column', md: 'row' });

  // Initialize form data when editing
  useEffect(() => {
    if (editingDeclaration) {
      console.log('Editing declaration:', editingDeclaration);
      console.log('Date valeur from backend:', editingDeclaration.date_valeur);
      
      // Format date for HTML date input (YYYY-MM-DD)
      let formattedDate = '';
      if (editingDeclaration.date_valeur && editingDeclaration.date_valeur !== 'null' && editingDeclaration.date_valeur !== 'undefined') {
        console.log('Processing date:', editingDeclaration.date_valeur);
        // Always convert to YYYY-MM-DD format
        const date = new Date(editingDeclaration.date_valeur);
        console.log('Created Date object:', date);
        if (!isNaN(date.getTime())) {
          formattedDate = date.toISOString().split('T')[0];
          console.log('Final formatted date:', formattedDate);
        } else {
          console.log('Invalid date object');
        }
      } else {
        console.log('No date value or null/undefined');
      }
      console.log('Formatted date for input:', formattedDate);
      
      setFormData({
        montant: editingDeclaration.montant?.toString() || '',
        montant_allouer: type === 'note de frais' ? '' : (editingDeclaration.montant_allouer?.toString() || ''),
        description: editingDeclaration.description || '',
        date_valeur: formattedDate,
        photo: null,
        commercial_affected: editingDeclaration.commercial_affected?.toString() || '',
      });
      setPreviewUrl(editingDeclaration.photo_url ? `${process.env.REACT_APP_API_URL || 'http://localhost:3002/api'}${editingDeclaration.photo_url}` : null);
      
      // Load existing declaration details
      loadDeclarationDetails(editingDeclaration.id);
    } else {
      setFormData({
        montant: '',
        montant_allouer: type === 'note de frais' ? '' : '',
        description: '',
        date_valeur: '',
        photo: null,
        commercial_affected: selectedCommercial || '',
      });
      setPreviewUrl(null);
      setSelectedDocument(null);
      setSelectedDocumentType(null);
    }
  }, [editingDeclaration, selectedCommercial]);

  // Load documents when modal opens
  useEffect(() => {
    if (isOpen && !documentsLoaded) {
      loadDocuments();
    }
  }, [isOpen, documentsLoaded]);

  // --- Effect to search when searchTerms change ---
  useEffect(() => {
    let isMounted = true;
    const doSearch = async (type, term) => {
      if (userRole !== 'Commercial') return;
      if (dbSearchRequested[type] && dbSearchActive[type] && term && term.length >= 3) {
        setSearchLoading(prev => ({ ...prev, [type]: true }));
        setSearchError(prev => ({ ...prev, [type]: null }));
        try {
          let results = [];
          if (type === 'facture') {
            results = await documentsAPI.searchFactures(term);
          } else if (type === 'devis') {
            results = await documentsAPI.searchDevis(term);
          } else if (type === 'proforma') {
            results = await documentsAPI.searchProforma(term);
          }
          console.log('searchResults', results);
          console.log('searchResults', results);
          
          if (isMounted) setSearchResults(prev => ({ ...prev, [type === 'facture' ? 'factures' : type === 'devis' ? 'devis' : 'proforma']: results }));

          setDocuments(prev => ({
            ...prev,
            [type]: results
          }));

        } catch (err) {
          if (isMounted) setSearchError(prev => ({ ...prev, [type]: 'Erreur lors de la recherche' }));
        } finally {
          if (isMounted) setSearchLoading(prev => ({ ...prev, [type]: false }));
          if (isMounted) setDbSearchRequested(prev => ({ ...prev, [type]: false }));
        }
      } else {

        setSearchResults(prev => ({ ...prev, [type]: [] }));
        setSearchError(prev => ({ ...prev, [type]: null }));
     
      }
    };
    // For each type, check if searchTerms[type] changed
    ['facture', 'devis', 'proforma'].forEach(type => {
      doSearch(type, searchTerms[type]);
    });
    return () => { isMounted = false; };
  }, [searchTerms, userRole, dbSearchRequested, dbSearchActive]);

  const loadDocuments = async () => {
    setLoadingDocuments(true);
    try {
      const [factures, devis, proforma] = await Promise.all([
        documentsAPI.getFactures(),
        documentsAPI.getDevis(),
        documentsAPI.getProforma()
      ]);

      console.log('Loaded documents:', { factures, devis, proforma });
      setDocuments({
        facture: factures || [],
        devis: devis || [],
        proforma: proforma || []
      });
      setDocumentsLoaded(true);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les documents',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingDocuments(false);
    }
  };

  const loadDeclarationDetails = async (declarationId) => {
    try {
      const details = await declarationDetailsAPI.getByDeclarationId(declarationId);
      const selected = {
        facture: [],
        devis: [],
        proforma: []
      };
      
      // For single document selection, take the first detail
      if (details.length > 0) {
        const firstDetail = details[0];
        // Find the document in our documents state
        const documentType = firstDetail.type;
        const document = documents[documentType]?.find(doc => doc.UUID === firstDetail.uuid_related);
        if (document) {
          setSelectedDocument(document);
          setSelectedDocumentType(documentType);
        }
      }
    } catch (error) {
      console.error('Error loading declaration details:', error);
    }
  };

  const refreshDocuments = async () => {
    setLoadingDocuments(true);
    try {
      await documentsAPI.refreshDocuments();
      await loadDocuments();
      toast({
        title: 'Succès',
        description: 'Documents actualisés',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error refreshing documents:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible d\'actualiser les documents',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingDocuments(false);
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'cheque': return 'Chèque';
      case 'effet': return 'Effet';
      case 'espece': return 'Espèce';
      case 'virement': return 'Virement';
      case 'compte client': return 'Compte Client';
      case 'versement deplacer': return 'Versement Déplacé';
      case 'note de frais': return 'Note de Frais';
      default: return type;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'cheque': return 'blue';
      case 'effet': return 'green';
      case 'espece': return 'orange';
      case 'virement': return 'purple';
      case 'compte client': return 'teal';
      case 'versement deplacer': return 'pink';
      case 'note de frais': return 'yellow';
      default: return 'gray';
    }
  };

  const handleInputChange = (field, value) => {
    console.log('handleInputChange:', field, value);
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDocumentSelection = (document) => {
    console.log('handleDocumentSelection called:', { document });
    setSelectedDocument(document);
     // Set document type based on selected document
  };

  const removeDocumentSelection = () => {
    console.log('removeDocumentSelection called');
    setSelectedDocument(null);
  
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        photo: file
      }));

      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const removePhoto = () => {
    setFormData(prev => ({
      ...prev,
      photo: null
    }));
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    // Reset the file input
    const fileInput = document.getElementById('photo-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const resetForm = () => {
    setFormData({
      montant: '',
      montant_allouer: type === 'note de frais' ? '' : '',
      description: '',
      date_valeur: '',
      photo: null,
      commercial_affected: selectedCommercial || '',
    });
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setSelectedDocument(null);
    setSelectedDocumentType(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields based on role
      if (!formData.montant || parseFloat(formData.montant) <= 0) {
        throw new Error('Le montant est requis et doit être supérieur à 0');
      }

      // For Technico: only montant and photo are required
      if (userRole === 'Technico') {
        if (!formData.photo && !editingDeclaration?.photo_url) {
          throw new Error('Une photo est requise pour tous les types de déclaration');
        }
      } else if (userRole === 'Commercial') {
        // For Commercial: all fields are required when creating new declarations, except for compte client
        // Skip photo validation if declaration is assigned by Technico (can't modify photo)
        if (type !== 'compte client' && !isAssignedDeclaration && !formData.photo && !editingDeclaration?.photo_url) {
          throw new Error('Une photo est requise pour tous les types de déclaration sauf compte client');
        }
        
        // If creating new declaration, all fields are required (except photo for compte client)
        if (!editingDeclaration) {
          if (!formData.description) {
            throw new Error('La description est requise');
          }
          if ((type === 'cheque' || type === 'effet') && !formData.date_valeur) {
            throw new Error('La date de valeur est requise');
          }
        }
      } else {
        // For other roles: all fields are required
        if ((type === 'cheque' || type === 'effet') && !formData.date_valeur) {
          throw new Error('La date de valeur est requise');
        }

        if (type !== 'compte client' && !formData.photo && !editingDeclaration?.photo_url) {
          throw new Error('Une photo est requise pour tous les types de déclaration sauf compte client');
        }

        if (!formData.description) {
          throw new Error('La description est requise');
        }
      }

              // For Commercial users or when editing, documents are required
        if (userRole === 'Commercial' || isEditing) {
          if (!selectedDocument) {
            throw new Error('Un document (facture, devis ou proforma) est requis');
          }
        }

      // Prepare declaration data
      const declarationData = {
        type: type,
        montant: parseFloat(formData.montant),
      };

      // Add optional fields only if they have values
      console.log('formData.montant_allouer:', formData.montant_allouer);
      // For "note de frais" type, montant_allouer should always be null
      declarationData.montant_allouer = type === 'note de frais' ? null : (formData.montant_allouer && formData.montant_allouer !== '' ? parseFloat(formData.montant_allouer) : null);
      if (formData.description) {
        declarationData.description = formData.description;
      }
      if (formData.date_valeur) {
        // Ensure date is in YYYY-MM-DD format
        const date = new Date(formData.date_valeur);
        declarationData.date_valeur = date.toISOString().split('T')[0];
      }

      // Add commercial assignment for Technico
      if (userRole === 'Technico' && formData.commercial_affected) {
        declarationData.commercial_affected = formData.commercial_affected;
      }

      // Add photo if exists
      if (formData.photo) {
        declarationData.photo = formData.photo;
      }

      console.log('Final declarationData:', declarationData);
      let result;
      if (isEditing) {
        // Update existing declaration
        result = await declarationsAPI.update(editingDeclaration.id, declarationData);
        
        // Update declaration details - Only for Commercial users
        if (userRole === 'Commercial') {
          await updateDeclarationDetails(editingDeclaration.id);
        }
        
        // Add a small delay to ensure backend has processed the details
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (onDeclarationUpdated) {
          onDeclarationUpdated(result);
        }
        toast({
          title: 'Succès',
          description: 'Déclaration modifiée avec succès',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new declaration
        console.log('Creating declaration with data:', declarationData);
        result = await declarationsAPI.create(declarationData);
        
        // Create declaration details - For Commercial users or when editing
        if (userRole === 'Commercial' || isEditing) {
          await createDeclarationDetails(result.id);
        }
        
        // Add a small delay to ensure backend has processed the details
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (onDeclarationAdded) {
          onDeclarationAdded(result);
        }
        toast({
          title: 'Succès',
          description: 'Déclaration ajoutée avec succès',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      // Reset form and close modal
      resetForm();
      onClose();

    } catch (error) {
      console.error('Error saving declaration:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la sauvegarde de la déclaration';
      toast({
        title: 'Erreur',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const createDeclarationDetails = async (declarationId) => {
    try {
      console.log('Creating declaration details for ID:', declarationId);
      console.log('Selected document:', selectedDocument);
      
      if (selectedDocument) {
        console.log('Creating detail for selected document:', selectedDocument);
        await declarationDetailsAPI.create(declarationId, selectedDocument.UUID, selectedDocumentType);
        console.log('Successfully created detail for:', selectedDocument);
      }
    } catch (error) {
      console.error('Error in createDeclarationDetails:', error);
      console.error('Error stack:', error.stack);
    }
  };

  const updateDeclarationDetails = async (declarationId) => {
    // First, get existing details
    try {
      const existingDetails = await declarationDetailsAPI.getByDeclarationId(declarationId);
      
      // Delete existing details
      for (const detail of existingDetails) {
        try {
          await declarationDetailsAPI.delete(detail.id);
        } catch (error) {
          console.error('Error deleting existing detail:', error);
        }
      }
      
      // Create new details
      await createDeclarationDetails(declarationId);
    } catch (error) {
      console.error('Error updating declaration details:', error);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleDocumentTypeSelection = (type) => {
    // If switching to a different type, clear the selected document
    if (selectedDocumentType && selectedDocumentType !== type) {
      setSelectedDocument(null);
    }
    setSelectedDocumentType(type);
  };

  const renderDocumentSection = (type, documents, selectedItems, searchControlsDirection) => {
    const searchTerm = searchTerms[type];
    const isOpen = dropdownOpen[type];
    const isDbSearchActive = dbSearchActive[type];
    // Ensure selectedItems is an array
    const safeSelectedItems = Array.isArray(selectedItems) ? selectedItems : [];

    // Use searchResults if available, otherwise default documents
    const isDbResults = userRole === 'Commercial' && isDbSearchActive && searchTerm && searchTerm.length >= 3 && searchResults[type].length > 0;
    const filteredDocuments =
      isDbResults
        ? searchResults[type]
        : Array.isArray(documents)
          ? documents.filter(doc => {
              const searchText = searchTerm.toLowerCase();
              const docNumber = type === 'facture' ? doc.Num_facture : type === 'devis' ? doc.Num_devis : type === 'proforma' ? doc.Num_facture : doc.UUID;
              const displayName = doc.display_name || docNumber;
              return docNumber.toLowerCase().includes(searchText) || 
                     displayName.toLowerCase().includes(searchText) ||
                     doc.date.toLowerCase().includes(searchText);
            })
          : [];

    // --- Fix: Always show selected documents, even if from backend search ---
    // Merge local and backend docs for selected display
    const allDocs = [
      ...(Array.isArray(documents) ? documents : []),
      ...(isDbResults ? searchResults[type] : [])
    ];
    // Remove duplicates by UUID
    const uniqueDocsMap = {};
    allDocs.forEach(doc => {
      if (doc && doc.UUID) uniqueDocsMap[doc.UUID] = doc;
    });
    const uniqueDocs = Object.values(uniqueDocsMap);
    const selectedDocsDisplay = uniqueDocs.filter(doc => safeSelectedItems.includes(doc.UUID));

    const handleSearchChange = (value) => {
      setSearchTerms(prev => ({
        ...prev,
        [type]: value
      }));
      // Reset backend search results if user changes input
      setSearchResults(prev => ({ ...prev, [type]: [] }));
      setDbSearchRequested(prev => ({ ...prev, [type]: false }));
      setSearchError(prev => ({ ...prev, [type]: null }));
    };

    const handleDropdownToggle = (open) => {
      setDropdownOpen(prev => ({
        ...prev,
        [type]: open
      }));
    };

    // --- Fix: Always allow selecting from backend or local results ---
    const handleDocumentSelect = (doc) => {
      handleDocumentSelection(doc);
      handleDropdownToggle(false);
      handleSearchChange('');
    };

    // --- New: handle checkbox and search button ---
    const handleDbSearchToggle = (e) => {
      if(!e.target.checked) {
        loadDocuments()
      }

      setDbSearchActive(prev => ({ ...prev, [type]: e.target.checked }));
      setDbSearchRequested(prev => ({ ...prev, [type]: false }));
      setSearchResults(prev => ({ ...prev, [type]: [] }));
      setSearchError(prev => ({ ...prev, [type]: null }));
    };
    const handleDbSearchClick = () => {
      console.log('handleDbSearchClick', type);
      setDbSearchRequested(prev => ({ ...prev, [type]: true }));
    };

    return (
      <FormControl>
        <FormLabel>
          {type === 'facture' ? 'Factures' : type === 'devis' ? 'Devis' : 'Proforma'}
          <Badge ml={2} colorScheme="blue" fontSize="xs">
            {selectedDocument ? '1 sélectionné' : '0 sélectionné'}
          </Badge>
        </FormLabel>
        {/* Selected Document Display */}
        {selectedDocument && (
          <Box mb={2}>
            <Text fontSize="sm" fontWeight="medium" mb={2}>Document sélectionné:</Text>
            <VStack spacing={1} align="stretch">
              <Flex 
                justify="space-between" 
                align="center" 
                bg="blue.50" 
                p={2} 
                borderRadius="md"
                border="1px"
                borderColor="blue.200"
              >
                <VStack spacing={0} align="start" flex={1}>
                  <Text fontSize="sm">
                    {selectedDocument.display_name || (type === 'facture' ? selectedDocument.Num_facture : type === 'devis' ? selectedDocument.Num_devis : type === 'proforma' ? selectedDocument.Num_facture : selectedDocument.UUID)}
                  </Text>
                  {selectedDocument.total_amount && (
                    <Text fontSize="xs" color="green.600" fontWeight="medium">
                      {parseFloat(selectedDocument.total_amount).toFixed(2)} MAD
                    </Text>
                  )}
                </VStack>
                <VStack spacing={1} align="end">
                  <Text fontSize="xs" color="gray.500">
                    {selectedDocument.date}
                  </Text>
                  <IconButton
                    size="xs"
                    icon={<CloseIcon />}
                    onClick={removeDocumentSelection}
                    colorScheme="red"
                    variant="ghost"
                  />
                </VStack>
              </Flex>
            </VStack>
          </Box>
        )}
        {/* Search and Select */}
        <Box position="relative">
          <Flex
            align={{ base: 'stretch', md: 'center' }}
            gap={2}
            mb={2}
            direction={searchControlsDirection}
            flexDirection={searchControlsDirection}
            wrap="wrap"
          >
            <Input
              placeholder={`Rechercher ${type === 'facture' ? 'factures' : type === 'devis' ? 'devis' : 'proforma'}...`}
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              onClick={() => handleDropdownToggle(true)}
              onFocus={() => handleDropdownToggle(true)}
              maxW={{ base: '100%', md: '250px' }}
              size="md"
              borderRadius="md"
              boxShadow="sm"
              mb={{ base: 2, md: 0 }}
            />
            <Tooltip label="Activez pour rechercher dans toute la base de données" hasArrow>
              <Checkbox
                isChecked={isDbSearchActive}
                onChange={handleDbSearchToggle}
                colorScheme="blue"
                size="md"
                ml={{ base: 0, md: 2 }}
                mb={{ base: 2, md: 0 }}
              >
                <Text fontSize="sm">Rechercher dans la base</Text>
              </Checkbox>
            </Tooltip>
            {isDbSearchActive && (
              <Tooltip label="Cliquez pour lancer la recherche avancée" hasArrow>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={handleDbSearchClick}
                  isLoading={searchLoading[type]}
                  disabled={!searchTerm || searchTerm.length < 3}
                  borderRadius="md"
                  ml={{ base: 0, md: 2 }}
                  px={4}
                  w={{ base: '100%', md: 'auto' }}
                >
                  Rechercher
                </Button>
              </Tooltip>
            )}
          </Flex>
          {isOpen && (
            <Box
              position="absolute"
              top="100%"
              left={0}
              right={0}
              bg="white"
              border="1px"
              borderColor="gray.200"
              borderRadius="md"
              maxH="260px"
              overflowY="auto"
              zIndex={10}
              boxShadow="lg"
            >
              {searchLoading[type] ? (
                <Flex align="center" justify="center" p={3}><Spinner size="sm" mr={2}/>Recherche...</Flex>
              ) : searchError[type] ? (
                <Text p={3} color="red.500" fontSize="sm">{searchError[type]}</Text>
              ) : filteredDocuments.length === 0 ? (
                <Text p={3} color="gray.500" fontSize="sm">
                  Aucun document trouvé
                </Text>
              ) : (
                <VStack spacing={0} align="stretch">
                  {isDbResults && (
                    <>
                      <Box px={3} py={1} bg="blue.50" borderTopRadius="md">
                        <Text fontSize="xs" color="blue.700" fontWeight="bold">Résultats de la base de données</Text>
                      </Box>
                      <Divider mb={1} />
                    </>
                  )}
                  {filteredDocuments.map((doc) => {
                    const isSelected = safeSelectedItems.includes(doc.UUID);
                    return (
                      <Box
                        key={doc.UUID}
                        p={2}
                        cursor="pointer"
                        bg={isSelected ? "blue.50" : "transparent"}
                        _hover={{ bg: isSelected ? "blue.100" : "gray.50" }}
                        onClick={() => {
                          handleDocumentSelect(doc);
                        }}
                        borderBottom="1px"
                        borderColor="gray.100"
                      >
                        <Flex justify="space-between" align="center">
                          <VStack spacing={0} align="start" flex={1}>
                            <Text fontSize="sm" fontWeight={isSelected ? "medium" : "normal"}>
                              {doc.display_name || (type === 'facture' ? doc.Num_facture : type === 'devis' ? doc.Num_devis : type === 'proforma' ? doc.Num_facture : doc.UUID)}
                            </Text>
                            {doc.total_amount && (
                              <Text fontSize="xs" color="green.600" fontWeight="medium">
                                {parseFloat(doc.total_amount).toFixed(2)} MAD
                              </Text>
                            )}
                          </VStack>
                          <Text fontSize="xs" color="gray.500" ml={2}>
                            {doc.date}
                          </Text>
                        </Flex>
                      </Box>
                    );
                  })}
                </VStack>
              )}
            </Box>
          )}
        </Box>
        {/* Close dropdown when clicking outside */}
        {isOpen && (
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={5}
            onClick={() => handleDropdownToggle(false)}
          />
        )}
      </FormControl>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent bg={bg}>
        <ModalHeader color={`${getTypeColor(type)}.500`}>
          {isEditing ? 'Modifier la déclaration' : 'Nouvelle déclaration'} - {getTypeLabel(type)}
          {isAssignedDeclaration && (
            <Text fontSize="sm" color="gray.500" mt={1}>
              (Assignée par un technicien - montant et photo non modifiables)
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={4} as="form" onSubmit={handleSubmit}>
            {/* Montant */}
            <FormControl isRequired>
              <FormLabel>
                Montant (MAD)
                {isAssignedDeclaration && <Text as="span" fontSize="sm" color="gray.500" ml={2}>(non modifiable)</Text>}
              </FormLabel>
              <InputGroup>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.montant}
                  onChange={(e) => handleInputChange('montant', e.target.value)}
                  placeholder="0.00"
                  size="lg"
                  isDisabled={isAssignedDeclaration}
                  bg={isAssignedDeclaration ? "gray.100" : "white"}
                />
                <InputRightElement>
                  <Text color="gray.500">MAD</Text>
                </InputRightElement>
              </InputGroup>
            </FormControl>

            {/* Montant Alloué */}
            <FormControl isRequired={type !== 'note de frais'}>
              <FormLabel>
                Montant Alloué (MAD)
                {type === 'note de frais' && <Text as="span" fontSize="sm" color="gray.500" ml={2}>(non applicable)</Text>}
              </FormLabel>
              <InputGroup>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max={formData.montant || undefined}
                  value={formData.montant_allouer}
                  onChange={(e) => handleInputChange('montant_allouer', e.target.value)}
                  placeholder="0.00"
                  size="lg"
                  isDisabled={type === 'note de frais'}
                  bg={type === 'note de frais' ? 'gray.100' : 'white'}
                />
                <InputRightElement>
                  <Text color="gray.500">MAD</Text>
                </InputRightElement>
              </InputGroup>
              {formData.montant && type !== 'note de frais' && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Maximum: {formData.montant} MAD
                </Text>
              )}
              {type === 'note de frais' && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Le montant alloué n'est pas applicable pour les notes de frais
                </Text>
              )}
            </FormControl>

            {/* Type Selection - Only for editing compte client declarations */}
            {isEditing && editingDeclaration?.type === 'compte client' && (
              <FormControl>
                <FormLabel>Type de déclaration</FormLabel>
                <Select
                  value={type}
                  onChange={(e) => {
                    if (onTypeChange) {
                      onTypeChange(e.target.value);
                    }
                  }}
                  size="lg"
                >
                  <option value="compte client">Compte Client</option>
                  <option value="cheque">Chèque</option>
                  <option value="effet">Effet</option>
                  <option value="espece">Espèce</option>
                  <option value="virement">Virement</option>
                </Select>
              </FormControl>
            )}

            {/* Commercial Assignment for Technico */}
            {userRole === 'Technico' && commercials && commercials.length > 0 && type != "note de frais" && (
              <FormControl>
                <FormLabel>Assigner à un commercial</FormLabel>
                <Select
                  value={formData.commercial_affected}
                  onChange={(e) => handleInputChange('commercial_affected', e.target.value)}
                  placeholder="Sélectionner un commercial"
                  size="lg"
                >
                  {commercials.map((commercial) => (
                    <option key={commercial.id_user} value={commercial.id_user}>
                      {commercial.nom} ({commercial.login})
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Description */}
            <FormControl isRequired={userRole === 'Commercial' && !editingDeclaration}>
              <FormLabel>Description {userRole === 'Technico' ? '(optionnel)' : (editingDeclaration && userRole === 'Commercial') ? '(optionnel)' : '(requis)'}</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Description de la déclaration..."
                size="lg"
                rows={3}
              />
            </FormControl>

            {/* Date de valeur - only for cheque and effet, or when editing compte client (since type can change) */}
            {(type === 'cheque' || type === 'effet' || (isEditing && editingDeclaration?.type === 'compte client')) && (
              <FormControl isRequired={userRole === 'Commercial' && !editingDeclaration}>
                <FormLabel>Date de valeur {userRole === 'Technico' ? '(optionnel)' : (editingDeclaration && userRole === 'Commercial') ? '(optionnel)' : '(requis)'}</FormLabel>
                <Input
                  type="date"
                  value={formData.date_valeur}
                  onChange={(e) => handleInputChange('date_valeur', e.target.value)}
                  size="lg"
                />
              </FormControl>
            )}

            {/* Document Selection - For Commercial users or when editing */}
            {(userRole === 'Commercial' || isEditing) && (
              <FormControl>
                <FormLabel>
                  Documents associés (requis)
                  <Button
                    size="xs"
                    ml={2}
                    onClick={refreshDocuments}
                    isLoading={loadingDocuments}
                    leftIcon={<RepeatIcon />}
                  >
                    Actualiser
                  </Button>
                </FormLabel>
                
                {/* Document Type Selection */}
                <VStack spacing={3} align="stretch" mb={4}>
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    Choisir le type de document:
                  </Text>
                  <HStack spacing={4}>
                    <Button
                      size="sm"
                      variant={selectedDocumentType === 'facture' ? 'solid' : 'outline'}
                      colorScheme={selectedDocumentType === 'facture' ? 'blue' : 'gray'}
                      onClick={() => handleDocumentTypeSelection('facture')}
                      flex={1}
                    >
                      Factures ({documents.facture.length})
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedDocumentType === 'devis' ? 'solid' : 'outline'}
                      colorScheme={selectedDocumentType === 'devis' ? 'blue' : 'gray'}
                      onClick={() => handleDocumentTypeSelection('devis')}
                      flex={1}
                    >
                      Devis ({documents.devis.length})
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedDocumentType === 'proforma' ? 'solid' : 'outline'}
                      colorScheme={selectedDocumentType === 'proforma' ? 'blue' : 'gray'}
                      onClick={() => handleDocumentTypeSelection('proforma')}
                      flex={1}
                    >
                      Proforma ({documents.proforma.length})
                    </Button>
                  </HStack>
                </VStack>

                {/* Show only the selected document type */}
                {selectedDocumentType && (
                  <Box
                    border="1px"
                    borderColor="blue.200"
                    borderRadius="md"
                    p={4}
                    bg="blue.50"
                  >
                    <Text fontSize="sm" fontWeight="medium" mb={3} color="blue.700">
                      Sélection de {selectedDocumentType === 'facture' ? 'factures' : selectedDocumentType === 'devis' ? 'devis' : 'proforma'}
                    </Text>
                    {renderDocumentSection(
                      selectedDocumentType, 
                      documents[selectedDocumentType], 
                      selectedDocument && selectedDocument.UUID ? [selectedDocument.UUID] : [],
                      searchControlsDirection
                    )}
                  </Box>
                )}

                {/* Show message when no document type is selected */}
                {!selectedDocumentType && (
                  <Box
                    border="2px dashed"
                    borderColor="gray.300"
                    borderRadius="md"
                    p={6}
                    textAlign="center"
                  >
                    <Text color="gray.500" fontSize="sm">
                      Veuillez sélectionner un type de document ci-dessus
                    </Text>
                  </Box>
                )}
              </FormControl>
            )}

            {/* Photo upload - required for all types except compte client */}
            <FormControl isRequired={type !== 'compte client'}>
              <FormLabel>
                Photo de la déclaration
                {type === 'compte client' && <Text as="span" fontSize="sm" color="gray.500" ml={2}>(optionnel)</Text>}
                {isAssignedDeclaration && <Text as="span" fontSize="sm" color="gray.500" ml={2}>(non modifiable)</Text>}
              </FormLabel>
              <VStack spacing={3}>
                {!previewUrl ? (
                  <Box
                    border="2px dashed"
                    borderColor={isAssignedDeclaration ? "gray.200" : "gray.300"}
                    borderRadius="md"
                    p={6}
                    textAlign="center"
                    w="full"
                    cursor={isAssignedDeclaration ? "not-allowed" : "pointer"}
                    _hover={{ borderColor: isAssignedDeclaration ? "gray.200" : `${getTypeColor(type)}.300` }}
                    onClick={isAssignedDeclaration ? undefined : () => document.getElementById('photo-upload').click()}
                    bg={isAssignedDeclaration ? "gray.50" : "transparent"}
                  >
                    <AddIcon boxSize={6} color={isAssignedDeclaration ? "gray.300" : "gray.400"} mb={2} />
                    <Text color={isAssignedDeclaration ? "gray.400" : "gray.500"}>
                      {isAssignedDeclaration ? "Photo non modifiable" : "Cliquez pour sélectionner une photo"}
                    </Text>
                    <Text fontSize="sm" color={isAssignedDeclaration ? "gray.300" : "gray.400"}>
                      {isAssignedDeclaration ? "Déclaration assignée par un technicien" : "JPG, PNG jusqu'à 5MB"}
                    </Text>
                  </Box>
                ) : (
                  <Box position="relative" w="full">
                    <Image
                      src={previewUrl}
                      alt="Preview"
                      w="full"
                      h="200px"
                      objectFit="cover"
                      borderRadius="md"
                    />
                    <IconButton
                      icon={<CloseIcon />}
                      onClick={removePhoto}
                      position="absolute"
                      top={2}
                      right={2}
                      colorScheme="red"
                      size="sm"
                      isRound
                      isDisabled={isAssignedDeclaration}
                      opacity={isAssignedDeclaration ? 0.5 : 1}
                    />
                  </Box>
                )}
                <input
                  id="photo-upload"
                  key={`photo-upload-${isEditing ? editingDeclaration?.id : 'new'}`}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  disabled={isAssignedDeclaration}
                />
              </VStack>
            </FormControl>

            {/* Submit Button */}
            <HStack spacing={3} w="full" pt={4}>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                flex={1}
                size="lg"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                colorScheme={getTypeColor(type)}
                flex={1}
                size="lg"
                isLoading={loading}
                loadingText={isEditing ? "Modification..." : "Enregistrement..."}
              >
                {isEditing ? 'Modifier' : 'Enregistrer'}
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default DeclarationFormModal; 